// /app/frontend/src/pages/risk/dpia/steps/DataStep.jsx
import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Plus, Trash2, Database } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

export default function DataStep({ formData, updateField }) {
  const addDataItem = () => {
    const newItem = {
      id: uuidv4(),
      data_collected: '',
      data_source: '',
      why_required: '',
      how_processed: '',
      storage_location: '',
      data_state: 'identifiable',
      retention_period: '',
      retention_reason: '',
    };
    updateField('data_items', [...formData.data_items, newItem]);
  };

  const updateDataItem = (itemId, field, value) => {
    const updated = formData.data_items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    );
    updateField('data_items', updated);
  };

  const removeDataItem = (itemId) => {
    updateField('data_items', formData.data_items.filter(item => item.id !== itemId));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Items</h3>
          <p className="text-sm text-muted-foreground">Document each type of personal data being processed</p>
        </div>
        <Button variant="default" size="sm" onClick={addDataItem} className="bg-teal-500 hover:bg-teal-600" data-testid="add-data-item-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Data Item
        </Button>
      </div>

      {formData.data_items.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="py-12 text-center">
            <Database className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground font-medium">No data items added yet</p>
            <p className="text-sm text-muted-foreground mt-1">Click "Add Data Item" to document the personal data being processed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {formData.data_items.map((item, idx) => (
            <Card key={item.id} className="border border-border">
              <CardHeader className="pb-2 bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Data Item #{idx + 1}</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeDataItem(item.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`remove-data-item-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Collected <span className="text-red-500">*</span></Label>
                    <Input 
                      placeholder="e.g., Full name, Email address, Phone number"
                      value={item.data_collected}
                      onChange={(e) => updateDataItem(item.id, 'data_collected', e.target.value)}
                      data-testid={`data-collected-${idx}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Source</Label>
                    <Input 
                      placeholder="e.g., Customer form, Third-party API"
                      value={item.data_source}
                      onChange={(e) => updateDataItem(item.id, 'data_source', e.target.value)}
                      data-testid={`data-source-${idx}`}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Why is this data required?</Label>
                  <Textarea 
                    placeholder="Explain the necessity of collecting this data..."
                    value={item.why_required}
                    onChange={(e) => updateDataItem(item.id, 'why_required', e.target.value)}
                    rows={2}
                    data-testid={`why-required-${idx}`}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Storage Location</Label>
                    <Input 
                      placeholder="e.g., AWS S3, On-premise server"
                      value={item.storage_location}
                      onChange={(e) => updateDataItem(item.id, 'storage_location', e.target.value)}
                      data-testid={`storage-location-${idx}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data State</Label>
                    <Select 
                      value={item.data_state}
                      onValueChange={(v) => updateDataItem(item.id, 'data_state', v)}
                    >
                      <SelectTrigger data-testid={`data-state-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="anonymised">Anonymised</SelectItem>
                        <SelectItem value="pseudonymised">Pseudonymised</SelectItem>
                        <SelectItem value="identifiable">Identifiable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Retention Period</Label>
                    <Input 
                      placeholder="e.g., 7 years"
                      value={item.retention_period}
                      onChange={(e) => updateDataItem(item.id, 'retention_period', e.target.value)}
                      data-testid={`retention-period-${idx}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
